import moment from 'moment'
import { emoteUrls } from '../extras/playerEmotes'
import { readPacket, writePacket } from '../packets'
import { storage } from '../storage'
import { uuid } from '../utils'
import { hashFile } from '../utils-client'
import { bootstrapGuestSession } from '../../client/auth-session'
import { ConnectionStateMachine } from '../../client/connection-state'
import { System } from './System'

const AUTH_TIMEOUT_MS = 15000
const SOCKET_TIMEOUT_MS = 15000
const SNAPSHOT_TIMEOUT_MS = 30000

/**
 * Client Network System
 *
 * - runs on the client
 * - provides abstract network methods matching ServerNetwork
 *
 */
export class ClientNetwork extends System {
  constructor(world) {
    super(world)
    this.ids = -1
    this.ws = null
    this.apiUrl = null
    this.id = null
    this.isClient = true
    this.queue = []
    this.attemptId = 0
    this.attempt = null
    this.hasSnapshot = false
    this.worldSignalReady = false
    this.gridSignalReady = false
    this.connection = new ConnectionStateMachine({ onChange: state => this.world.emit('connection', state) })
  }

  async init({ wsUrl, name, avatar }) {
    // ?fresh — нулевой аккаунт: сбрасываем токен до подключения и чистим
    // адресную строку, чтобы перезагрузка не плодила новые аккаунты
    let fresh = false
    try {
      const params = new URLSearchParams(window.location.search)
      if (params.has('fresh')) {
        fresh = true
        params.delete('fresh')
        const rest = params.toString()
        window.history.replaceState(null, '', window.location.pathname + (rest ? `?${rest}` : ''))
      }
    } catch (err) {
      // не браузер (нода-клиент) — просто продолжаем
    }
    this.connectionOptions = { wsUrl, name, avatar, fresh }
    this.world.on('ready', this.onWorldReady)
    this.world.on('assets:progress', this.onAssetProgress)
    this.world.on('assets:error', this.onAssetError)
    this.world.on('assets:complete', this.onAssetsComplete)
    this.world.livekit?.on?.('status', this.onVoiceStatus)
    await this.connect()
  }

  connect = async ({ reconnecting = false } = {}) => {
    this.cleanupAttempt({ closeSocket: true })
    const id = ++this.attemptId
    const controller = new AbortController()
    const attempt = { id, controller, timers: new Set(), intentionalClose: false }
    this.attempt = attempt
    this.connection.start({ reconnecting })
    this.worldSignalReady = false
    this.gridSignalReady = false
    this.connection.complete('app')
    this.connection.complete('core')
    const timeout = setTimeout(() => controller.abort('timeout'), AUTH_TIMEOUT_MS)
    attempt.timers.add(timeout)
    try {
      const { wsUrl, name, avatar, fresh } = this.connectionOptions
      const url = await bootstrapGuestSession({
        wsUrl, name, avatar, fresh, storage, signal: controller.signal,
        // В браузере transport нет — bootstrapGuestSession возьмёт globalThis.fetch.
        fetchImpl: this.world.transport?.fetch,
        onPhase: (phase, status) => {
          if (this.attempt === attempt) this.connection[status === 'complete' ? 'complete' : 'begin'](phase)
        },
      })
      clearTimeout(timeout)
      attempt.timers.delete(timeout)
      if (this.attempt !== attempt || controller.signal.aborted) return
      this.openSocket(url, attempt)
    } catch (error) {
      if (this.attempt !== attempt || controller.signal.aborted && controller.signal.reason !== 'timeout') return
      const offline = globalThis.navigator?.onLine === false
      const code = offline ? 'offline' : controller.signal.reason === 'timeout' || error?.name === 'AbortError' ? 'network_timeout' : error?.code || 'network_error'
      if (code === 'session_rejected' || code === 'session_migration') this.connection.fail(code)
      else this.failRecoverable(code)
    }
  }

  openSocket(url, attempt) {
    this.connection.begin('socket')
    // Билет едет в заголовке Cookie: в ноде его подставляет transport, в
    // браузере — сам браузер. Коды readyState у обеих реализаций стандартные.
    const SocketImpl = this.world.transport?.WebSocket || WebSocket
    const ws = new SocketImpl(url)
    this.ws = ws
    ws.binaryType = 'arraybuffer'
    ws.addEventListener('open', this.onOpen)
    ws.addEventListener('message', this.onPacket)
    ws.addEventListener('close', this.onClose)
    ws.addEventListener('error', this.onSocketError)
    const timer = setTimeout(() => {
      if (this.attempt !== attempt || ws.readyState === WebSocket.OPEN) return
      this.failRecoverable('network_timeout')
      attempt.intentionalClose = true
      ws.close()
    }, SOCKET_TIMEOUT_MS)
    attempt.socketTimer = timer
    attempt.timers.add(timer)
  }

  onOpen = () => {
    const attempt = this.attempt
    if (!attempt) return
    if (attempt.socketTimer) clearTimeout(attempt.socketTimer)
    attempt.timers.delete(attempt.socketTimer)
    this.connection.complete('socket')
    this.connection.begin('snapshot')
    const timer = setTimeout(() => {
      if (this.attempt !== attempt) return
      this.failRecoverable('snapshot_timeout')
      attempt.intentionalClose = true
      this.ws?.close()
    }, SNAPSHOT_TIMEOUT_MS)
    attempt.snapshotTimer = timer
    attempt.timers.add(timer)
  }

  retryConnection = () => this.connect({ reconnecting: this.hasSnapshot })

  cancelConnection = () => {
    this.cleanupAttempt({ closeSocket: true })
    this.connection.cancel()
  }

  disconnectToStart = () => {
    this.cleanupAttempt({ closeSocket: true })
    this.connection.cancel()
  }

  clearOwnedSessionRuntime = () => {
    this.cleanupAttempt({ closeSocket: true })
    this.hasSnapshot = false
    this.worldSignalReady = false
    this.gridSignalReady = false
    this.id = null
    this.screenData = {}
    this.avatarGallery = []
    this.world.livekit?.room?.disconnect?.()
    this.world.entities?.destroy?.()
    this.world.blueprints?.destroy?.()
    this.world.chat?.clear?.()
    this.connection.cancel()
  }

  cleanupAttempt({ closeSocket = false } = {}) {
    const attempt = this.attempt
    if (attempt) {
      attempt.intentionalClose = true
      attempt.controller.abort('cancelled')
      for (const timer of attempt.timers) clearTimeout(timer)
      attempt.timers.clear()
    }
    if (closeSocket && this.ws) {
      this.detachSocket(this.ws)
      if (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING) this.ws.close()
      this.ws = null
    }
    this.attempt = null
    this.world.loader?.cancelPreload?.()
  }

  detachSocket(ws) {
    ws.removeEventListener('open', this.onOpen)
    ws.removeEventListener('message', this.onPacket)
    ws.removeEventListener('close', this.onClose)
    ws.removeEventListener('error', this.onSocketError)
  }

  preFixedUpdate() {
    this.flush()
  }

  send(name, data) {
    // console.log('->', name, data)
    const packet = writePacket(name, data)
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return false
    this.ws.send(packet)
    return true
  }

  async upload(file) {
    {
      // first check if we even need to upload it
      const hash = await hashFile(file)
      const ext = file.name.split('.').pop().toLowerCase()
      const filename = `${hash}.${ext}`
      const url = `${this.apiUrl}/upload-check?filename=${filename}`
      const resp = await fetch(url)
      const data = await resp.json()
      if (data.exists) return // console.log('already uploaded:', filename)
    }
    // then upload it
    const form = new FormData()
    form.append('file', file)
    const url = `${this.apiUrl}/upload`
    await fetch(url, {
      method: 'POST',
      body: form,
    })
  }

  enqueue(method, data) {
    this.queue.push([method, data])
    // До первого тика мира очередь разбирает таймер. Стартовый экран пускает
    // игрока только после обработанного snapshot, но flush() обычно зовёт
    // preFixedUpdate — а тикать мир начинает после входа. Без этой подкачки
    // взаимная блокировка: пакеты лежат в очереди, «Снимок мира» висит вечно.
    // Node-клиенты и тесты крутят тик сами, поэтому там дедлока не видно.
    if (!this.world.frame && !this.pendingFlush) {
      this.pendingFlush = setTimeout(() => {
        this.pendingFlush = null
        if (!this.world.frame) this.flush()
      }, 0)
    }
  }

  flush() {
    while (this.queue.length) {
      try {
        const [method, data] = this.queue.shift()
        this[method]?.(data)
      } catch (err) {
        console.error(err)
      }
    }
  }

  getTime() {
    return (performance.now() + this.serverTimeOffset) / 1000 // seconds
  }

  onPacket = e => {
    const [method, data] = readPacket(e.data)
    this.enqueue(method, data)
    // console.log('<-', method, data)
  }

  onSnapshot(data) {
    const attempt = this.attempt
    if (attempt?.snapshotTimer) clearTimeout(attempt.snapshotTimer)
    attempt?.timers.delete(attempt?.snapshotTimer)
    this.connection.complete('snapshot')
    this.connection.begin('assets')
    const worldTimer = setTimeout(() => {
      if (this.attempt !== attempt || this.connection.status === 'ready') return
      this.failRecoverable('snapshot_timeout')
    }, SNAPSHOT_TIMEOUT_MS)
    if (attempt) {
      attempt.worldTimer = worldTimer
      attempt.timers.add(worldTimer)
    }
    if (this.hasSnapshot) {
      this.world.entities.destroy()
      this.world.blueprints.destroy()
      this.world.chat.clear()
    }
    this.hasSnapshot = true
    this.id = data.id
    this.serverTimeOffset = data.serverTime - performance.now()
    this.apiUrl = data.apiUrl
    this.maxUploadSize = data.maxUploadSize
    // Headless clients can provide an internal asset endpoint that bypasses
    // the public reverse proxy. Browser clients do not set one and continue
    // to use the server-provided public URL.
    this.world.assetsUrl = this.world.assetsUrl || data.assetsUrl

    // preload environment model and avatar
    // if (this.world.environment.base) {
    //   this.world.loader.preload('model', this.world.environment.base.model)
    // }
    if (data.settings.avatar) {
      this.world.loader.preload('avatar', data.settings.avatar.url)
    }
    // preload some blueprints
    for (const item of data.blueprints) {
      if (item.preload && !item.disabled) {
        if (item.model) {
          const type = item.model.endsWith('.vrm') ? 'avatar' : 'model'
          this.world.loader.preload(type, item.model)
        }
        if (item.script) {
          this.world.loader.preload('script', item.script)
        }
        for (const value of Object.values(item.props || {})) {
          if (value === undefined || value === null || !value?.url || !value?.type) continue
          this.world.loader.preload(value.type, value.url)
        }
      }
    }
    // preload emotes
    for (const url of emoteUrls) {
      this.world.loader.preload('emote', url)
    }
    // preload local player avatar
    for (const item of data.entities) {
      if (item.type === 'player' && item.owner === this.id) {
        const url = item.sessionAvatar || item.avatar
        this.world.loader.preload('avatar', url)
      }
    }
    this.world.loader.execPreload()

    this.world.collections.deserialize(data.collections)
    this.world.settings.deserialize(data.settings)
    this.world.settings.setHasAdminCode(data.hasAdminCode)
    this.world.chat.deserialize(data.chat)
    this.world.ai.deserialize(data.ai)
    this.world.blueprints.deserialize(data.blueprints)
    this.world.entities.deserialize(data.entities)
    this.world.livekit?.deserialize(data.livekit)
    // галерея персонажей для HUD (пул стартовых аватаров мира)
    this.avatarGallery = Array.isArray(data.avatarGallery) ? data.avatarGallery : []
  }

  // Данные экранов: складываем последнее состояние по виду и будим
  // подписчиков. Экран может как подписаться, так и прочитать разом.
  onScreenData = data => {
    if (!data?.kind) return
    if (!this.screenData) this.screenData = {}
    this.screenData[data.kind] = data.payload
    if (data.kind === 'player') {
      this.gridSignalReady = true
      this.maybeCompleteWorld()
    }
    this.world.emit(`screen:${data.kind}`, data.payload)
  }

  onSettingsModified = data => {
    this.world.settings.set(data.key, data.value)
  }

  onChatAdded = msg => {
    this.world.chat.add(msg, false)
  }

  onChatCleared = () => {
    this.world.chat.clear()
  }

  onBlueprintAdded = blueprint => {
    this.world.blueprints.add(blueprint)
  }

  onBlueprintModified = change => {
    this.world.blueprints.modify(change)
  }

  onEntityAdded = data => {
    this.world.entities.add(data)
  }

  onEntityModified = data => {
    const entity = this.world.entities.get(data.id)
    if (!entity) return console.error('onEntityModified: no entity found', data)
    entity.modify(data)
  }

  onEntityEvent = event => {
    const [id, version, name, data] = event
    const entity = this.world.entities.get(id)
    entity?.onEvent(version, name, data)
  }

  onEntityRemoved = id => {
    this.world.entities.remove(id)
  }

  onPlayerTeleport = data => {
    this.world.entities.player?.teleport(data)
  }

  onPlayerPush = data => {
    this.world.entities.player?.push(data.force)
  }

  onPlayerSessionAvatar = data => {
    this.world.entities.player?.setSessionAvatar(data.avatar)
  }

  onLiveKitLevel = data => {
    this.world.livekit?.setLevel(data.playerId, data.level)
  }

  onMute = data => {
    this.world.livekit.setMuted(data.playerId, data.muted)
  }

  onPong = time => {
    this.world.stats?.onPong(time)
  }

  onKick = code => {
    // Кик — осознанный отказ сервера (вы уже в мире / мир полон), а не
    // сбой связи. Ведём его в машину состояний: она залипает в этом
    // состоянии, запрещает автоповтор и не даёт следующему onClose
    // подменить причину на «связь прервана».
    const attempt = this.attempt
    if (attempt) attempt.intentionalClose = true
    this.connection.kick(code)
    this.world.emit('kick', code)
  }

  onClose = code => {
    this.world.chat.add({
      id: uuid(),
      from: null,
      fromId: null,
      body: 'Связь с миром прервана.',
      createdAt: moment().toISOString(),
    })
    this.world.emit('disconnect', code || true)
    const attempt = this.attempt
    if (attempt?.intentionalClose || this.connection.status === 'cancelled') return
    this.cleanupAttempt()
    if (this.connection.status !== 'error') this.connection.fail(code?.code === 1008 ? 'ticket_expired' : 'ws_closed')
    if (!this.connection.retryTimer) this.connection.scheduleRetry(() => this.connect({ reconnecting: this.hasSnapshot }))
  }

  onSocketError = () => {
    this.failRecoverable('ws_rejected')
  }

  failRecoverable(code) {
    if (this.connection.status === 'error') return
    this.connection.fail(code)
    this.connection.scheduleRetry(() => this.connect({ reconnecting: this.hasSnapshot }))
  }

  onWorldReady = ready => {
    if (!ready) return
    this.worldSignalReady = true
    if (this.connection.phases.assets.status === 'running') {
      this.connection.complete('assets')
      this.connection.begin('world')
    }
    this.maybeCompleteWorld()
  }

  onAssetProgress = ({ loaded, total }) => this.connection.counter('assets', loaded, total)
  onAssetError = () => this.failRecoverable('asset_failure')
  onAssetsComplete = ({ failed = 0 } = {}) => {
    if (!failed) {
      this.connection.complete('assets')
      this.connection.begin('world')
      this.maybeCompleteWorld()
    }
  }
  maybeCompleteWorld() {
    if (!this.worldSignalReady || !this.gridSignalReady || this.connection.phases.assets.status !== 'complete') return
    const attempt = this.attempt
    if (attempt?.worldTimer) clearTimeout(attempt.worldTimer)
    attempt?.timers.delete(attempt?.worldTimer)
    this.connection.ready()
  }
  onVoiceStatus = status => {
    if (!status?.available) return
    if (status.connected) this.connection.complete('voice')
    else if (status.error) this.connection.optionalUnavailable('voice', 'Голос временно недоступен; мир продолжает работу.')
    else if (status.connecting) this.connection.begin('voice')
  }

  destroy() {
    this.world.off('ready', this.onWorldReady)
    this.world.off('assets:progress', this.onAssetProgress)
    this.world.off('assets:error', this.onAssetError)
    this.world.off('assets:complete', this.onAssetsComplete)
    this.world.livekit?.off?.('status', this.onVoiceStatus)
    this.cleanupAttempt({ closeSocket: true })
    this.connection.destroy()
  }
}
